import { type BaseSyntheticEvent, type ReactNode } from 'react';
import type {
  FieldErrors,
  FieldValues,
  Path,
  UseFormRegister,
  UseFormReturn,
} from 'react-hook-form';
import { FormProvider } from 'react-hook-form';
import { clsx } from 'clsx';
import { Button } from '@repo/ui/Button';
import { Icon } from '@repo/ui/Icon';
import { InputControl } from '@repo/ui/Input/InputControl';
import { InputField } from '@repo/ui/Input/InputField';
import { InputGroup } from '@repo/ui/Input/InputGroup';
import { InputLabel } from '@repo/ui/Input/InputLabel';
import { InputMessage } from '@repo/ui/Input/InputMessage';
import { InputRoot } from '@repo/ui/Input/InputRoot';
import { Flex } from '@repo/ui/Flex';
import { IconButton } from '@repo/ui/IconButton';
import { Popover } from '@repo/ui/Popover';
import { PopoverRoot } from '@repo/ui/Popover/PopoverRoot';
import { PopoverTrigger } from '@repo/ui/Popover/PopoverTrigger';
import { PopoverContentSimple } from '@repo/ui/Popover/PopoverContentSimple';
import s from './Login.module.css';

export type LoginFieldType = 'EMAIL' | 'ZIPCODE' | 'ORDER_NUMBER';

export interface LoginTexts {
  title: string;
  subtitle: string;
  submitButton: string;
  submitErrorMessage: string;
  policyDescription: string;
  policyUrl: string;
  help: string;
  info: string;
  close: string;
  fieldLabel: (field: LoginFieldType) => string;
  fieldHelp: (field: LoginFieldType) => string;
}

export interface LoginHeaderProps {
  title: string;
  subtitle: string;
}

export interface LoginFormProps<T extends FieldValues = FieldValues> {
  fields: LoginFieldType[];
  errors: FieldErrors<T>;
  register: UseFormRegister<T>;
  handleSubmit: (e?: BaseSyntheticEvent) => Promise<void>;
  isLoading: boolean;
  isValid: boolean;
  isError: boolean;
  isCentered?: boolean;
  methods: UseFormReturn<T>;
  texts: LoginTexts;
  renderHeader?: (props: LoginHeaderProps) => ReactNode;
  renderSubsections?: Array<() => ReactNode>;
}

/**
 * Pure presentational LoginForm component.
 * Decoupled from pdp-specific dependencies by accepting text rendering,
 * header, and subsections as props.
 *
 * Generic over form values type T to avoid type assertions in consumers.
 */
export const Login = <T extends FieldValues = FieldValues>({
  fields,
  errors,
  register,
  handleSubmit,
  isLoading,
  isValid,
  isError,
  isCentered,
  methods,
  texts,
  renderHeader,
  renderSubsections,
}: LoginFormProps<T>) => {
  const defaultHeader = ({ title, subtitle }: LoginHeaderProps) => (
    <Flex direction="column" gap="g-12">
      <h1 className={s.h1} data-xb-field="title">
        {title}
      </h1>
      <h2 data-xb-field="subtitle">{subtitle}</h2>
    </Flex>
  );

  const header = renderHeader ?? defaultHeader;

  return (
    <Flex
      gap="g-24"
      padding="p-32"
      direction="column"
      className={clsx(s.wrapper, isCentered && s.centered)}
    >
      <FormProvider {...methods}>
        <form className={s.form} onSubmit={handleSubmit}>
          {header({ title: texts.title, subtitle: texts.subtitle })}

          <div className={s.inputContainer}>
            {fields.map((field) => (
              <InputRoot
                key={field}
                width="full"
                variant={errors[field] ? 'error' : 'default'}
              >
                <InputGroup>
                  <InputField>
                    <InputLabel htmlFor={field}>
                      {texts.fieldLabel(field)}
                    </InputLabel>
                    <InputControl id={field} {...register(field as Path<T>)} />
                  </InputField>
                  <PopoverRoot>
                    <PopoverTrigger asChild={true}>
                      <IconButton
                        icon="question"
                        label={texts.help}
                        variant="tertiary"
                      />
                    </PopoverTrigger>
                    <Popover
                      placement="left"
                      align="center"
                      ariaLabel={texts.info}
                    >
                      <PopoverContentSimple closeButtonLabel={texts.close}>
                        {texts.fieldHelp(field)}
                      </PopoverContentSimple>
                    </Popover>
                  </PopoverRoot>
                </InputGroup>
                {errors[field] && (
                  <InputMessage>
                    <Icon name="warning" size="font" />
                    {errors[field]?.message as string}
                  </InputMessage>
                )}
              </InputRoot>
            ))}
          </div>
          <Button
            isFullWidth={true}
            disabled={isLoading || !isValid}
            isLoading={isLoading}
            type="submit"
            size="lg"
            data-xb-field="submitButton"
            data-xb-group="brandColor"
          >
            {texts.submitButton}
          </Button>
          {isError && (
            <div className={s.error} role="alert">
              {texts.submitErrorMessage}
            </div>
          )}
        </form>

        {texts.policyDescription && texts.policyUrl && (
          <div
            className={s.policy}
            aria-live="polite"
            data-xb-field="policyLink"
          >
            <a href={texts.policyUrl} target="_blank" rel="noopener noreferrer">
              {texts.policyDescription}
            </a>
          </div>
        )}
      </FormProvider>

      {renderSubsections?.map((renderSubsection, index) => (
        <div key={index}>{renderSubsection()}</div>
      ))}
    </Flex>
  );
};
